/**
 * ItineraryLimitModal
 *
 * Shown automatically when the backend returns 402 ITINERARY_LIMIT_EXCEEDED.
 * Listens for the 'parkpro:itinerary-limit-reached' custom event dispatched
 * by apiClient.js and presents a contextual upgrade prompt.
 *
 * Mount once inside AppChrome so it's available across all authenticated pages.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, TrendingUp } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import BaseModal from '../ui/BaseModal';

const ItineraryLimitModal = () => {
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const navigate = useNavigate();
  const { planDisplayName } = useUser();

  useEffect(() => {
    const handleLimitReached = (e) => {
      setUsage(e.detail?.usage ?? null);
      setOpen(true);
    };

    window.addEventListener('parkpro:itinerary-limit-reached', handleLimitReached);
    return () => window.removeEventListener('parkpro:itinerary-limit-reached', handleLimitReached);
  }, []);

  const used  = usage?.monthly_itineraries ?? '—';
  const limit = usage?.monthly_limit ?? '—';

  const handleUpgrade = () => {
    setOpen(false);
    navigate('/agent/settings?tab=subscription');
  };

  return (
    <BaseModal isOpen={open} onClose={() => setOpen(false)} size="sm" showClose={true}>
      <BaseModal.Body className="text-center">
        <div className="flex justify-center items-center mx-auto mb-3 w-12 h-12 bg-amber-100 rounded-full">
          <TrendingUp className="w-6 h-6 text-amber-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">
          Monthly Itinerary Limit Reached
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          You've used <span className="font-medium text-gray-700">{used}</span> of{' '}
          <span className="font-medium text-gray-700">{limit}</span> itineraries this month
          on your <span className="font-medium text-gray-700">{planDisplayName}</span> plan.
        </p>

        <div className="mt-5 space-y-2">
          <button
            onClick={handleUpgrade}
            className="flex gap-2 justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            type="button"
          >
            <Zap className="w-4 h-4" />
            Upgrade My Plan
          </button>
          <button
            onClick={handleUpgrade}
            className="flex justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
            type="button"
          >
            Purchase Add-On Itineraries
          </button>
          <button
            onClick={() => setOpen(false)}
            className="py-1 w-full text-sm text-gray-400 transition-colors hover:text-gray-600"
            type="button"
          >
            Dismiss
          </button>
        </div>
      </BaseModal.Body>
    </BaseModal>
  );
};

export default ItineraryLimitModal;
